import dayjs from 'dayjs';

import { getRandomId } from './general';

// Redirect key override for pre-redirect state persistence
let redirectKeyOverride: string | null = null;

export const setRedirectKeyOverride = (key: string | null) => {
  redirectKeyOverride = key;
};

export const getRedirectKeyOverride = () => redirectKeyOverride;

const setTabSessionCreated = (tabId: string) => {
  if (localStorage) {
    const currentTime = new Date().toISOString();
    localStorage.setItem(`tabSession-${tabId}-createdAt`, currentTime);
  }
};

const getAllOldTabIds = () => {
  const oldTabIds: string[] = [];
  const localStorageEntries = Object.entries(localStorage);
  for (const entry of localStorageEntries) {
    const match = entry[0].match(/^tabSession-([\da-z]+)-createdAt$/);
    if (match && match[1]) {
      const tabId = match[1];
      const createdAt = entry[1];
      if (dayjs().isAfter(dayjs(createdAt).add(30, 'days'))) {
        oldTabIds.push(tabId);
      }
    }
  }
  return oldTabIds;
};

const cleanUpOldRedirectKeys = () => {
  if (!localStorage) return;

  const oneDay = 24 * 60 * 60 * 1000;
  const now = Date.now();

  Object.keys(localStorage).forEach(storageKey => {
    // Match redirect-scoped keys: ord_xxx or pi_xxx prefixes
    if (storageKey.match(/^(ord_|pi_)[A-Za-z0-9_-]+-/)) {
      // Check if there's a tabSession marker for this redirect key
      const keyMatch = storageKey.match(/^([^-]+)-/);
      if (keyMatch) {
        const redirectId = keyMatch[1];
        const createdAt = localStorage.getItem(
          `tabSession-${redirectId}-createdAt`
        );
        if (createdAt) {
          const age = now - new Date(createdAt).getTime();
          if (age > oneDay) {
            // Cleanup old redirect key
            localStorage.removeItem(storageKey);
          }
        }
      }
    }
  });

  // Also cleanup orphaned tabSession markers for redirect keys
  Object.keys(localStorage).forEach(storageKey => {
    if (storageKey.match(/^tabSession-(ord_|pi_)[A-Za-z0-9_-]+-createdAt$/)) {
      try {
        const createdAt = localStorage.getItem(storageKey);
        if (createdAt) {
          const age = now - new Date(createdAt).getTime();
          if (age > oneDay) {
            localStorage.removeItem(storageKey);
          }
        }
      } catch (e) {
        // Ignore
      }
    }
  });
};

const cleanUpOldTabSessions = () => {
  if (!localStorage) {
    return;
  }
  const oldTabIds = getAllOldTabIds();
  for (const tabId of oldTabIds) {
    const localStorageEntries = Object.entries(localStorage);
    for (const entry of localStorageEntries) {
      if (new RegExp(`^${tabId}-`).test(entry[0])) {
        localStorage.removeItem(entry[0]);
      }
    }
    localStorage.removeItem(`tabSession-${tabId}-createdAt`);
  }

  cleanUpOldRedirectKeys();
};

export const getTabScopedKey = (key: string) => {
  if (typeof window === 'undefined') {
    // SSR
    return key;
  }

  // Priority 1: Check if we have a redirect key override
  // (Used when saving state before redirect)
  if (redirectKeyOverride) {
    return `${redirectKeyOverride}-${key}`;
  }

  // Priority 2: Check if we're returning from a payment redirect
  const urlParams = new URLSearchParams(window.location.search);
  const revolutOrderId = urlParams.get('_rp_oid');
  const stripePaymentIntent = urlParams.get('payment_intent');
  const redirectId = revolutOrderId || stripePaymentIntent;

  if (redirectId) {
    return `${redirectId}-${key}`;
  }

  // Priority 3: Normal flow - tab-based scoping
  let tabId = sessionStorage.getItem('tabId');
  if (!tabId) {
    tabId = getRandomId();
    sessionStorage.setItem('tabId', tabId);
    setTabSessionCreated(tabId);
    cleanUpOldTabSessions();
  }
  return `${tabId}-${key}`;
};
